import React, { memo } from 'react';
import { SkylarqAvatar } from './SkylarqAvatar';
import { TypewriterText } from './TypewriterText';
import { CHAT_TEXT } from '../../../constants/chat';

interface AssistantMessageProps {
  children: React.ReactNode;
  className?: string;
  /** Enable typewriter animation */
  isAnimating?: boolean;
  /** Text content for typewriter (required if isAnimating) */
  animationText?: string;
  /** Callback when typewriter animation completes */
  onAnimationComplete?: () => void;
  /** Animation speed in ms per character */
  animationSpeed?: number;
}

/**
 * Assistant message bubble with Skylarq branding.
 * Supports optional typewriter animation for dynamic text reveal.
 */
export const AssistantMessage = memo<AssistantMessageProps>(({
  children,
  className = '',
  isAnimating = false,
  animationText,
  onAnimationComplete,
  animationSpeed = 6,
}) => (
  <div className={`flex gap-3 ${className}`}>
    <SkylarqAvatar />
    <div className="flex-1 min-w-0 pt-0.5">
      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
        {CHAT_TEXT.BRAND_NAME_UPPER}
      </p>
      <div className="text-[14px] text-gray-800 leading-relaxed">
        {isAnimating && animationText ? (
          <TypewriterText
            text={animationText}
            onComplete={onAnimationComplete}
            speed={animationSpeed}
          />
        ) : (
          children
        )}
      </div>
    </div>
  </div>
));

AssistantMessage.displayName = 'AssistantMessage';

interface UserMessageProps {
  children: React.ReactNode;
  className?: string;
  /** Data attribute for scroll targeting */
  dataUserMessage?: boolean;
}

/**
 * User message bubble with dark background.
 * Aligned to the right side of the chat.
 */
export const UserMessage = memo<UserMessageProps>(({
  children,
  className = '',
  dataUserMessage = true,
}) => (
  <div
    className={`flex justify-end ${className}`}
    {...(dataUserMessage && { 'data-user-message': true })}
  >
    <div className="bg-gray-900 text-white px-4 py-2 rounded-2xl rounded-br-sm max-w-[85%]">
      <p className="text-[14px]">{children}</p>
    </div>
  </div>
));

UserMessage.displayName = 'UserMessage';
